import { apiBase } from 'api/api';
import { handleBlobReportResponse } from 'util/downloadReport';
import { cleanAddressField } from 'util/helper';
import { ApiResponse } from 'api/interface/api';
import {
    GetPermitRequest,
    GetPermitResponse,
    BackofficeCreatePermitRequest,
    BackofficeCreatePermitResponse,
    BackofficeUpdatePermitRequest,
    BackofficeUpdatePermitResponse,
    BackofficeGetListOfPermitsRequest,
    GetListOfPermitsResponse,
    GetPermitTypesResponse,
    SendReceiptCopyRequest,
    SendReceiptCopyResponse,
    BackofficeGetPermitReportRequest,
    BackofficeGetPermitCountRequest,
    BackofficeGetPermitCountResponse,
    Permit1,
    PermitReasonDetailsResponse,
    PermitReason,
    CreatePermitReasonResponse,
    PermitTypeDetail,
    GetPermitTypeDetailResponse,
    CreatePermitTypeResponse,
} from 'api/interface/permit';
import { getCETTimestamp } from 'util/dateTimeConverter';
import { EasyParkParkingSession } from 'pages/loggedIn/backoffice/permits/interfaces';

const PERMIT_REPORT_ROW_LIMIT =
    'Unable to generate report. The amount of rows exceeded 200,000 — please apply filters to minimize the amount.';

const generatePermitReport = async (
    req: BackofficeGetPermitReportRequest
): Promise<ApiResponse> => {
    const res = await apiBase.get(
        `/v3/permits/report?from=${req.from}&to=${req.to}&status=${req.status}&type=${req.type}&limit=${req.limit}&offset=${req.offset}&sort_col=${req.sortcol}&sort_order=${req.sortorder}&search_term=${req.search_term}&organisation_ids=${req.organisation_ids}&parkinglot_ids=${req.parkinglot_ids}`,
        {
            responseType: 'blob',
            timeout: 0,
        }
    );

    const result = await handleBlobReportResponse(res, {
        non200Error: PERMIT_REPORT_ROW_LIMIT,
    });

    return {
        error: result.error,
        statusCode: result.statusCode,
    } as ApiResponse;
};

const getPermit = async (req: GetPermitRequest): Promise<GetPermitResponse> => {
    const res = await apiBase.get(`/v3/permits/${Number(req.id)}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            ...res.data,
            street_name: cleanAddressField(res.data.street_name),
            street_number: cleanAddressField(res.data.street_number),
            postal_code: cleanAddressField(res.data.postal_code),
            city: cleanAddressField(res.data.city),
            start_date: getCETTimestamp(res.data.start_date),
            end_date: getCETTimestamp(res.data.end_date),
        },
    } as GetPermitResponse;
};

const createPermit = async (
    req: BackofficeCreatePermitRequest
): Promise<BackofficeCreatePermitResponse> => {
    const res = await apiBase.post('/v3/permits', {
        ...req,
    });

    if (res.status !== 200) {
        return {
            error: res.data?.message ?? 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as BackofficeCreatePermitResponse;
};

const updatePermit = async (
    req: BackofficeUpdatePermitRequest
): Promise<BackofficeUpdatePermitResponse> => {
    const res = await apiBase.put(`/v3/permits/${Number(req.id)}`, {
        ...req,
    });

    if (res.status !== 200) {
        return {
            error: res.data?.message || 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as BackofficeUpdatePermitResponse;
};

const getListOfPermits = async (
    req: BackofficeGetListOfPermitsRequest
): Promise<GetListOfPermitsResponse> => {
    const res = await apiBase.get(`/v3/permits`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            data: [], // Return an empty array for data
            total: 0, // Set total to 0 in case of an error
        } as GetListOfPermitsResponse;
    }

    return {
        error: null,
        statusCode: res.status,
        data: res.data.data.map((item: Permit1) => ({
            permit: {
                id: item.permit.id,
                regno: item.permit.regno,
                type: item.permit.type,
                status: item.permit.status,
                start_date: getCETTimestamp(item.permit.start_date),
                end_date: getCETTimestamp(item.permit.end_date),
            },
            user: {
                name: item.user.name,
                username: item.user.username,
                user_note: item.user.user_note,
            },
            address: {
                address_name: item.address.address_name,
                street_name: cleanAddressField(item.address.street_name),
                street_number: cleanAddressField(item.address.street_number),
                zipcode: cleanAddressField(item.address.zipcode),
                city: cleanAddressField(item.address.city),
            },
            organisation: {
                name: item.organisation.name,
            },
        })),
        total: res.data.total,
    } as GetListOfPermitsResponse;
};

const getPermitCount = async (
    req: BackofficeGetPermitCountRequest
): Promise<BackofficeGetPermitCountResponse> => {
    const res = await apiBase.get(`/v3/permits/count`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            total: 0,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        total: res.data.total,
    };
};

const getPermitTypes = async (): Promise<GetPermitTypesResponse> => {
    const res = await apiBase.get(`/v3/permits/types`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetPermitTypesResponse;
};

const sendReceiptCopy = async (
    req: SendReceiptCopyRequest
): Promise<SendReceiptCopyResponse> => {
    const res = await apiBase.post(`/backoffice/tickets/receipt`, {
        id: req.id,
        user_email: req.user_email,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {},
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.success,
    } as SendReceiptCopyResponse;
};

const getEasyParkPermitPrice = async (
    req: Pick<
        EasyParkParkingSession,
        'camera_id' | 'area_no' | 'reg_no' | 'start_time' | 'end_time'
    >
) => {
    const body = {
        cci_id: req.camera_id,
        area_no: req.area_no,
        reg_no: req.reg_no,
        start: req.start_time,
        end: req.end_time,
    };
    const res = await apiBase.post('/easypark/price', body);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {},
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

const endEasyParkSession = async (
    req: Pick<EasyParkParkingSession, 'id' | 'price' | 'end_time'>
) => {
    const body = {
        price_kr: req.price,
        end_time: req.end_time,
    };
    const res = await apiBase.post(
        `/v3/permits/easypark/backoffice/stop/${req.id}`,
        body
    );
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {},
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

// Permit Reasons API Functions
const getPermitReasonsList = async (params: {
    limit: number;
    offset: number;
    searchTerm: string;
    sortColumn?: string;
    sortOrder?: string;
}): Promise<PermitReasonDetailsResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', params.limit.toString());
    queryParams.append('offset', params.offset.toString());
    queryParams.append('search_term', params.searchTerm);

    if (params.sortColumn) {
        queryParams.append('sort_column', params.sortColumn);
    }
    if (params.sortOrder) {
        queryParams.append('sort_order', params.sortOrder);
    }

    const res = await apiBase.get(
        `/v3/platform/permit-reasons?${queryParams.toString()}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [] as PermitReason[],
                count: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            ...res.data,
            data: (res.data.data || []).map((item: PermitReason) => ({
                ...item,
                key_values:
                    typeof item.key_values === 'string' && item.key_values
                        ? JSON.parse(item.key_values)
                        : (item.key_values ?? {}),
            })),
        },
    } as PermitReasonDetailsResponse;
};

const createPermitReason = async (
    permitReasonData: PermitReason
): Promise<CreatePermitReasonResponse> => {
    const res = await apiBase.post(`/v3/platform/permit-reasons`, {
        name: permitReasonData.name,
        description: permitReasonData.description,
        key_values: JSON.stringify(permitReasonData.key_values),
    });

    if (res.status > 299) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                id: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreatePermitReasonResponse;
};

const updatePermitReason = async (
    permitReasonData: PermitReason
): Promise<ApiResponse> => {
    const res = await apiBase.put(
        `/v3/platform/permit-reasons/${permitReasonData.id}`,
        {
            id: permitReasonData.id,
            name: permitReasonData.name,
            description: permitReasonData.description,
            key_values: JSON.stringify(permitReasonData.key_values),
        }
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const deletePermitReason = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/platform/permit-reasons/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

// Permit Types API Functions
const getPermitTypeDetails = async (
    id: number
): Promise<GetPermitTypeDetailResponse> => {
    const res = await apiBase.get(`/v3/permits/types/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {} as PermitTypeDetail,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetPermitTypeDetailResponse;
};

const createPermitType = async (
    data: Omit<PermitTypeDetail, 'id'>
): Promise<CreatePermitTypeResponse> => {
    const res = await apiBase.post(`/v3/permits/types`, {
        name: data.name,
        description: data.description,
        approval: data.approval || 'neutral',
        translation_key_values: data.translation_key_values,
    });

    if (res.status > 299) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                id: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreatePermitTypeResponse;
};

const updatePermitType = async (
    id: number,
    data: PermitTypeDetail
): Promise<ApiResponse> => {
    const res = await apiBase.put(`/v3/permits/types/${id}`, {
        name: data.name,
        description: data.description,
        approval: data.approval || 'neutral',
        translation_key_values: data.translation_key_values,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const deletePermitType = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/permits/types/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

export const backofficePermitApi = {
    generatePermitReport,
    getPermit,
    createPermit,
    updatePermit,
    getListOfPermits,
    getPermitCount,
    getPermitTypes,
    sendReceiptCopy,
    getEasyParkPermitPrice,
    endEasyParkSession,
    getPermitReasonsList,
    createPermitReason,
    updatePermitReason,
    deletePermitReason,
    getPermitTypeDetails,
    createPermitType,
    updatePermitType,
    deletePermitType,
};
